import { useEffect, useState } from 'react';
import { LINK_LEAD, LINK_LEAD_STAYS, cardNotAcceptedLine, dogCard, isClosedStatus, shapeMessage, stayDay, withLinkLine, type CardProof, type DogCard } from '@bbbk/schema';
import { MONTHS_ABBR } from '../../lib/dates';
import { ProofFull } from './ProofImage';
import { Icon } from '../common';
import { dublinDay, slotMessage } from './MoneyCard';
import { DecideBlock, DecideSkeleton, DropZone, SelfServiceCard, SelfServiceCardNa, Shot, TrailRow, TrailThumb, type Band, type Pill, type TrailClass } from './SelfServiceCard';

// THE VACCINATIONS CARD (vaccination-cards AC13–AC17; G28) — standalone in the
// Self-service column, under the Money card, and built from its parts
// (Freddi, 2026-10-13: "the vaccination card should appear as a standalone
// card but should reuse all components from the deposit proof card").
//
// Shut, the band says where the booking's dogs stand: the one dog whose card
// waits, *1/2* in date, *All in date*. Opened, one row per dog: the card's
// thumbnail, who put it there and when, one pill — Valid until, To check, Not
// accepted, Not uploaded. A row opens to the card large with what the state
// needs: the date to confirm and Accept · Not accepted (AC4, AC6); the answer
// and its Undo (AC5); her own drop zone on every state short of accepted (AC8).
//
// The rule is `dogCard` in @bbbk/schema, the one the customer's drawer and the
// upload endpoints read, so this card cannot say To check while the phone
// offers an upload.

const day = dublinDay;

/**
 * What Patricia types, in the dog's own `"MAR 28"` shape: the month and year
 * however written — *Mar 2028*, *MAR 28*, *03/28*, *03/2028*, *march 2028* —
 * or null.
 */
export function cardDate(raw: string): string | null {
  const s = raw.trim().toLowerCase();
  let month: number | null = null;
  let year: number | null = null;
  const named = /^([a-z]{3,})\.?\s+(\d{2}|\d{4})$/.exec(s);
  const numeric = /^(\d{1,2})\s*\/\s*(\d{2}|\d{4})$/.exec(s);
  if (named) {
    const i = MONTHS_ABBR.findIndex((m) => m.toLowerCase() === named[1]!.slice(0, 3));
    if (i >= 0) { month = i + 1; year = Number(named[2]); }
  } else if (numeric) {
    month = Number(numeric[1]); year = Number(numeric[2]);
  }
  if (month === null || year === null || month < 1 || month > 12) return null;
  // The dog's date holds two digits of a year in this century: a four-digit year outside it is not a
  // date — "Mar 1999" was stored as March 2099 and read as in date (PR #260 review).
  if (year >= 100 && (year < 2000 || year > 2099)) return null;
  return `${MONTHS_ABBR[month - 1]!.toUpperCase()} ${String(year % 100).padStart(2, '0')}`;
}

/** `"MAR 28"` → *Mar 2028*, the way the prototype prints a card's date. */
export function cardDateLabel(due: string): string {
  const m = /^([A-Za-z]{3}) (\d{2})$/.exec(due.trim());
  if (!m) return due;
  const i = MONTHS_ABBR.findIndex((x) => x.toLowerCase() === m[1]!.toLowerCase());
  return i < 0 ? due : `${MONTHS_ABBR[i]} 20${m[2]}`;
}

const monthIdx = (due: string | null | undefined): number | null => {
  const d = due ? cardDate(due) : null;
  if (!d) return null;
  return (2000 + Number(d.slice(4))) * 12 + MONTHS_ABBR.findIndex((x) => x.toUpperCase() === d.slice(0, 3));
};
const isoMonthIdx = (iso: string) => Number(iso.slice(0, 4)) * 12 + Number(iso.slice(5, 7)) - 1;

/** A card date she may confirm: a month and a year, not already past (Edge cases). */
export function confirmable(raw: string, today: string): string | null {
  const d = cardDate(raw);
  const idx = monthIdx(d);
  return d && idx !== null && idx >= isoMonthIdx(today) ? d : null;
}

export interface VaxDog {
  id: string;
  name: string;
  /**
   * The date the dog is judged by: the earlier of its annual and kennel-cough dates, as the phone reads
   * it (PR #260 review) — the annual alone when no kennel-cough date is on file, since the desk's one
   * date covers both (Freddi, 2026-10-13, option 1).
   */
  due: string | null;
  card: DogCard;
  /** Valid on the stay's first day — an accepted card or a typed date that runs past check-in. */
  inDate: boolean;
  /** Its date has already passed — not valid today, whatever the stay (Freddi, 2026-10-13). */
  expired: boolean;
}

export interface VaxState {
  dogs: VaxDog[];
  /** The dog whose card is the move now — the first to check, refused, missing or running out. */
  lead: VaxDog | null;
  tone: 'blue' | 'gold' | 'green' | 'grey';
  /**
   * The stay the words may name — its check-in, while it is still to come and not declined, cancelled
   * or over — else null (audit, 2026-10-15: a past stay's day, and "the booking", for none to come).
   */
  stay: string | null;
  /** That stay is booked, not a request still waiting: only then "See you on". */
  booked: boolean;
}

/**
 * What the card is drawn from: a booking — its dogs, judged against its check-in — or a household
 * with no stay open (Freddi, 2026-10-13), its dogs judged against today. A card is the dog's.
 */
export interface VaxSubject {
  dogs?: readonly { id: string; name: string; vaccinesDue?: string | null; kcDue?: string | null }[];
  cards?: CardProof[];
  checkIn: string | null;
  status?: string;
}

/** The earlier of two dates in the dog's shape; a missing one counts for nothing. */
const earlier = (a: string | null, b: string | null): string | null => {
  const ia = monthIdx(a);
  const ib = monthIdx(b);
  if (ia === null) return ib === null ? a : b;
  return ib !== null && ib < ia ? b : a;
};

/**
 * The household pane's stay: the first still to come, not begun and not closed — a stay the dogs are in
 * now has been judged already (PR #260 review). Null with none.
 */
export function householdNextStay(bookings: readonly { checkIn: string; status: string }[], today: string): string | null {
  return bookings.filter((x) => x.checkIn >= today && !isClosedStatus(x.status as never)).map((x) => x.checkIn).sort()[0] ?? null;
}

export function vaxState(b: VaxSubject, today: string): VaxState {
  const cards: CardProof[] = b.cards ?? [];
  // Judged against a stay still to come — never a declined, cancelled or finished one's dates (PR #275
  // review): with none, against today, so a card in date today is never said to have run out.
  const status = b.status ?? 'confirmed';
  const stay = b.checkIn && b.checkIn > today && !isClosedStatus(status as never) && status !== 'collected' ? b.checkIn : null;
  const start = isoMonthIdx(stay ?? today);
  const dogs: VaxDog[] = (b.dogs ?? []).map((d) => {
    const card = dogCard(cards, { id: d.id, vaccinesDue: d.vaccinesDue });
    const due = earlier(d.vaccinesDue ?? null, d.kcDue ?? null);
    const idx = monthIdx(due);
    const inDate = (card.state === 'accepted' || card.state === 'typed') && idx !== null && idx >= start;
    const expired = (card.state === 'accepted' || card.state === 'typed') && idx !== null && idx < isoMonthIdx(today);
    return { id: d.id, name: d.name, due, card, inDate, expired };
  });
  const first = (pick: (x: VaxDog) => boolean) => dogs.find(pick) ?? null;
  const lead = first((x) => x.card.state === 'to_check')
    ?? first((x) => x.card.state === 'not_accepted' || x.card.state === 'paused')
    ?? first((x) => x.card.state === 'none')
    // Run out before running out, as the band names them (PR #275 review).
    ?? first((x) => x.expired)
    ?? first((x) => !x.inDate);
  const tone = !lead ? 'green' : lead.card.state === 'to_check' ? 'blue' : lead.card.state === 'paused' ? 'grey' : 'gold';
  return { dogs, lead, tone, stay, booked: !!stay && status !== 'requested' };
}

/** The band: the one dog, or how many, whose card is the move — or All in date. The figure is in date of the dogs. */
export function vaxBand(s: VaxState): Band {
  const inDate = s.dogs.filter((d) => d.inDate).length;
  const figure = `${inDate}/${s.dogs.length}`;
  const named = (of: VaxDog[], one: (n: string) => string, many: (k: number) => string) => (of.length === 1 ? one(of[0]!.name) : many(of.length));
  const toCheck = s.dogs.filter((d) => d.card.state === 'to_check');
  if (toCheck.length) return { tone: 'warn', sentence: named(toCheck, (n) => `${n} to check`, (k) => `${k} cards to check`), figure, small: 'in date' };
  const refused = s.dogs.filter((d) => d.card.state === 'not_accepted' || d.card.state === 'paused');
  if (refused.length) return { tone: 'warn', sentence: named(refused, (n) => `${n} not accepted`, (k) => `${k} cards not accepted`), figure, small: 'in date' };
  const missing = s.dogs.filter((d) => d.card.state === 'none');
  if (missing.length) return { tone: 'warn', sentence: named(missing, (n) => `${n} · no card`, (k) => `${k} cards missing`), figure, small: 'in date' };
  // RUN OUT IS SAID AS RUN OUT (audit, 2026-10-15): "before the stay" is only for a card still in date today.
  const expired = s.dogs.filter((d) => d.expired);
  if (expired.length) return { tone: 'warn', sentence: named(expired, (n) => `${n}’s card has run out`, (k) => `${k} cards have run out`), figure, small: 'in date' };
  const lapsing = s.dogs.filter((d) => !d.inDate);
  if (lapsing.length) return { tone: 'warn', sentence: named(lapsing, (n) => `${n} runs out before the stay`, (k) => `${k} cards run out before the stay`), figure, small: 'in date' };
  return { tone: 'ok', sentence: 'All in date', figure, small: 'in date' };
}

/** The sign-in that sent the card the Vaccinations card is about — waiting or refused — or null. */
export function cardSender(b: VaxSubject, today: string): string | null {
  const lead = vaxState(b, today).lead;
  // With nothing to chase, the all-good is about the newest card Patricia accepted (PR #267 review).
  const p = lead ? lead.card.proof : [...(b.cards ?? [])].filter((c) => c.verifiedAt && c.uploadedRole === 'customer').sort((x, y) => y.createdAt.localeCompare(x.createdAt))[0];
  return p && p.uploadedRole === 'customer' ? p.uploadedBy : null;
}

const named = (names: string[]) => (names.length <= 1 ? names[0] ?? 'the dogs' : `${names.slice(0, -1).join(', ')} & ${names[names.length - 1]}`);

/**
 * THE CARDS' WORDS for the slot under the band — one shape for every state
 * (messages-one-shape): the fact, the sign-up link for a person with no
 * account, the ask, the sign-off. Never sent for her (231).
 */
export function vaxMessage(s: VaxState, b: VaxSubject, first: string, link: string | null = null): string {
  const { lead } = s;
  // A STAY THAT HAS BEGUN, IS OVER, OR IS NOT BOOKED (audit, 2026-10-15): no "See you on", no day named
  // for it, and with none to come the link line offers stays to book rather than "the booking". A request
  // not yet answered is not booked either (PR #275 review): it keeps its date only where the date is the
  // fact — "runs out before 3 Nov" — so a card in date today is never said to have run out.
  const linkLead = s.booked ? LINK_LEAD : LINK_LEAD_STAYS;
  // With no stay open the words name no day (Freddi, 2026-10-13: the card lives on the household too).
  if (!lead) {
    const all = s.dogs;
    const until = all.length === 1 && all[0]!.due ? ` until ${cardDateLabel(cardDate(all[0]!.due) ?? all[0]!.due)}` : '';
    const fact = `${named(all.map((d) => d.name))} ${all.length === 1 ? 'is' : 'are'} all good${until}`;
    return shapeMessage({ first, fact, ask: s.booked && s.stay ? `See you on ${stayDay(s.stay)}.` : null, link, linkLead });
  }
  if (lead.card.state === 'to_check') {
    // ONE SENDER PER MESSAGE (PR #267 review): the cards waiting from whoever sent the lead's, so the
    // words never speak to John about Maeve's card.
    const from = lead.card.proof?.uploadedBy;
    const waiting = s.dogs.filter((d) => d.card.state === 'to_check' && d.card.proof?.uploadedBy === from).map((d) => d.name);
    return waiting.length === 1
      ? shapeMessage({ first, fact: `got ${waiting[0]}’s card`, ask: 'I’ll check it and confirm.', link, linkLead })
      : shapeMessage({ first, fact: 'got the dogs’ cards', ask: 'I’ll check them and confirm.', link, linkLead });
  }
  // PAUSED IS THE THREAD (AC9; PR #260 review): the app takes no more cards from them, so the words
  // never send them back to it — and carry no link line, whoever the person is (messages-one-shape AC5).
  if (lead.card.state === 'paused') return shapeMessage({ first, fact: `I’ll sort ${lead.name}’s card out with you here`, ask: 'Could you send me a clear photo of the up-to-date card?' });
  if (lead.card.state === 'not_accepted') return withLinkLine(lead.card.proof?.rejectedReason ?? cardNotAcceptedLine(first, lead.name), link, linkLead);
  if (lead.card.state === 'none') {
    const forStay = s.booked && s.stay ? ` for the stay on ${stayDay(s.stay, false)}` : '';
    return shapeMessage({ first, fact: `we don’t have ${lead.name}’s vaccination card yet${forStay}`, ask: 'Could you upload it in the app?', link, linkLead });
  }
  const fact = !lead.expired && s.stay ? `${lead.name}’s vaccination card runs out before ${stayDay(s.stay, false)}` : `${lead.name}’s vaccination card has run out`;
  return shapeMessage({ first, fact, ask: 'Could you upload the up-to-date one in the app?', link, linkLead });
}


interface VaxTrailRow {
  dog: VaxDog;
  cls: TrailClass;
  title: string;
  sub: string;
  pill: Pill;
}

// THE ROW IS THE DOG'S NAME (the prototype's own rows; a 340px column holds no more beside the
// pill): the state and who sent the card ride the line under it — *Maeve uploaded · 3 Sep*,
// *Patricia added · just now* (AC17) — and an in-date card leads that line with its date,
// *Valid until Mar 2028* (AC16), under the pill the prototype draws: *Valid*.
function rowOf(d: VaxDog, first: string, nameOf?: (email: string) => string | null): VaxTrailRow {
  const p = d.card.proof;
  // THE LINE UNDER THE NAME, SHORT (Freddi, 2026-10-13: "short, apt, articulate"). The pill carries the
  // state or the date, so this says only what the pill cannot: who sent the card and when, or what is
  // wrong — a few words, never the date again.
  // WHOEVER SENT IT (PR #260 review; Freddi, 2026-10-13): the person on the household whose sign-in
  // sent the card — the first contact only when the sender is no longer on it.
  const from = p ? `From ${p.uploadedRole === 'desk' ? 'Patricia' : nameOf?.(p.uploadedBy) ?? first} · ${day(p.createdAt)}` : null;
  switch (d.card.state) {
    case 'to_check':
      return { dog: d, cls: 'read', title: d.name, sub: from!, pill: { tone: 'warn', word: 'To check' } };
    case 'not_accepted':
      return { dog: d, cls: 'no', title: d.name, sub: `Not accepted · ${day(p!.rejectedAt ?? p!.createdAt)}`, pill: { tone: 'bad', word: 'Not accepted' } };
    case 'paused':
      return { dog: d, cls: 'no', title: d.name, sub: 'Paused · sort it in the thread', pill: { tone: 'bad', word: 'Not accepted' } };
    case 'none':
      return { dog: d, cls: 'mute', title: d.name, sub: 'No card yet', pill: { tone: 'mute', word: 'Not uploaded' } };
    default: {
      // THE SYRINGE AND THE DATE, NOT A WORD (Freddi, 2026-10-13: "instead of showing valid pill at the
      // desk for accepted vaccinations lets show the vacine icon and the date it expires (same as
      // customer view)"): in its state's tone — green, the due tone short of the stay, red run out.
      const pill = (tone: Pill['tone']): Pill => ({ tone, word: d.due ? cardDateLabel(d.due) : 'Valid', glyph: 'syringe' });
      if (d.expired) return { dog: d, cls: 'no', title: d.name, sub: 'Expired · new card needed', pill: pill('bad') };
      const sub = !d.inDate ? 'Runs out before the stay' : d.card.state === 'accepted' && p ? `Accepted · ${day(p.verifiedAt ?? p.createdAt)}` : 'On file · no card';
      return { dog: d, cls: 'seen', title: d.name, sub, pill: pill(d.inDate ? 'ok' : 'todo') };
    }
  }
}

export interface VaccinationsCardProps {
  /** A booking, or a household with no stay open (`VaxSubject`). */
  booking: VaxSubject;
  /** The customer's first name — the rows and the lines to send name them. */
  first: string;
  /** The first name of the household's person with this sign-in, or null — who sent a card. */
  nameOf?: (email: string) => string | null;
  today: string;
  busy: boolean;
  /** The number of the person the message is for. */
  phone?: string | null;
  /** The sign-up link for a person with no account, `LINK_PENDING`, or null (messages-one-shape AC2). */
  link?: string | null;
  /** Makes the link when Patricia taps WhatsApp (AC2a). */
  issueLink?: () => Promise<string | null>;
  /** Accept, with the date she confirms in the dog's `"MAR 28"` shape (AC4). */
  onAccept: (proofId: string, validUntil: string) => void;
  onReject: (proofId: string) => void;
  /** The × on the card: remove it, and the upload area comes back (Freddi, 2026-10-13). */
  onRemove: (proofId: string) => void;
  onUpload: (dogId: string, file: File) => void;
  /** Test seams: every row open; the remove confirm already showing for that card. */
  rowsOpen?: boolean;
  confirmRemove?: string | null;
  /** The booking re-reading after a verdict — the decision shows as a skeleton till it lands (Freddi, 2026-10-14). */
  settling?: boolean;
  /** Test seam: the verdict pressed. */
  pendingVerdict?: { proofId: string; verdict: 'accept' | 'reject' } | null;
}

export function VaccinationsCardNa({ why }: { why: string }) {
  return <SelfServiceCardNa subject="vax" kind="vax ss-vax" title="Vaccinations" why={why} />;
}

/** Shut first, like the Money card (ruled 2026-10-11): the band carries the state. */
export function VaccinationsCard({ defaultOpen = false, focusDog, ...props }: VaccinationsCardProps & { defaultOpen?: boolean; focusDog?: string | null }) {
  // A bubble about a dog's card opens this unfolded, on that dog's row (Freddi, 2026-10-14).
  const [open, setOpen] = useState(defaultOpen);
  if ((props.booking.dogs ?? []).length === 0) return <VaccinationsCardNa why="No dogs on this booking" />;
  return <VaccinationsCardView {...props} focusDog={focusDog ?? null} open={open} onToggle={() => setOpen((o) => !o)} />;
}

export function VaccinationsCardView({ booking, first, nameOf, today, busy, phone, link = null, issueLink, onAccept, onReject, onRemove, onUpload, open, onToggle, rowsOpen = false, confirmRemove = null, focusDog = null, pendingVerdict = null, settling = false }: VaccinationsCardProps & { open: boolean; onToggle: () => void; focusDog?: string | null }) {
  const s = vaxState(booking, today);
  // A CANCELLED STAY IS READ-ONLY, as the Money card's is: no answers, no drop zone, no undo.
  const closed = booking.status === 'cancelled';
  const band = vaxBand(s);
  const rows = s.dogs.map((d) => rowOf(d, first, nameOf));
  // CARDS LOAD ONCE THEY CAN BE SEEN (PR #255 review, #42).
  const [seen, setSeen] = useState(open);
  useEffect(() => { if (open) setSeen(true); }, [open]);
  const [rowOpen, setRowOpen] = useState<Record<string, boolean>>(focusDog ? { [focusDog]: true } : {});
  // THE VERDICT'S MOTION, AS THE MONEY CARD'S (Freddi, 2026-10-14): Accepting… with the spinner while
  // the server works, a skeleton while the booking re-reads, then the row as the booking now says; the
  // dog's row is pinned open at the press.
  type Verdict = 'accept' | 'reject';
  const [acting, setActing] = useState<{ proofId: string; verdict: Verdict } | null>(pendingVerdict);
  useEffect(() => { if (acting && !busy && !settling) setActing(null); }, [acting, busy, settling]);
  const decide = (dogId: string, proofId: string, verdict: Verdict, run: () => void) => {
    setRowOpen((cur) => ({ ...cur, [dogId]: true }));
    setActing({ proofId, verdict });
    run();
  };
  const [removing, setRemoving] = useState<string | null>(confirmRemove);
  const [full, setFull] = useState<CardProof | null>(null);
  // THE DATE IS HERS TO CONFIRM (AC4): prefilled with the customer's claim, and
  // it belongs to one card — a new card starts from its own claim.
  const [typed, setTyped] = useState<{ proofId: string; value: string } | null>(null);

  const drop = (d: VaxDog) => (
    <DropZone className="vx-drop" title={d.card.proof ? 'Upload the card' : 'No card yet'} sub="A photo or a PDF of the card" busy={busy} onFile={(f) => onUpload(d.id, f)} />
  );
  // ONE AREA, AND ITS × (Freddi, 2026-10-13: "there should not be an uploaded attachment and a blank empty
  // state underneath to add more screenshots, there should be a single area with the screenshot, an x to
  // remove and once removed you can add another"). The Money card's own × and its question, beneath the
  // card; removed, the row has no card and its area is the upload zone again.
  const shot = (p: CardProof, alt: string, load: boolean, muted = false) => (
    <>
      <Shot id={p.id} mime={p.mime} alt={alt} muted={muted} load={load}
        corner={!closed && <button type="button" className="mc-remove" aria-label="Remove this card" title="Remove this card" disabled={busy} onClick={() => setRemoving(p.id)}><Icon name="close" size={12} /></button>} />
      {removing === p.id && (
        <div className="mc-remove-ask">
          <span><b>Remove this card?</b> The upload area comes back for another.</span>
          <span className="mc-remove-acts">
            <button type="button" className="btn quiet-danger mc-remove-yes" disabled={busy} onClick={() => { setRemoving(null); onRemove(p.id); }}>Remove</button>
            <button type="button" className="btn quiet mc-remove-no" onClick={() => setRemoving(null)}>Keep</button>
          </span>
        </div>
      )}
    </>
  );

  return (
    <SelfServiceCard subject="vax" kind="vax ss-vax" tone={s.tone} open={open} onToggle={onToggle} title="Vaccinations" band={band}
      message={closed ? null : slotMessage(first, phone, (l) => vaxMessage(s, booking, first, l), link, issueLink)}
      after={full && <ProofFull id={full.id} mime={full.mime} alt="The vaccination card" onClose={() => setFull(null)} />}>
      {rows.map((r) => {
        const d = r.dog;
        const p = d.card.proof;
        // The dog whose card is the move starts open, as the drawing has it.
        const isOpen = rowsOpen || (rowOpen[d.id] ?? s.lead?.id === d.id);
        const load = seen && isOpen;
        const alt = `${d.name}’s vaccination card`;
        const value = p && typed?.proofId === p.id ? typed.value : p?.claimedValidUntil ? cardDateLabel(p.claimedValidUntil) : '';
        const date = confirmable(value, today);
        return (
          <TrailRow key={d.id} cls={r.cls} uploaded={p !== null} title={r.title} sub={r.sub} pill={r.pill} open={isOpen}
            verdict={d.card.state === 'not_accepted' || d.card.state === 'paused' || d.expired ? 'bad' : d.card.state === 'accepted' || d.card.state === 'typed' ? 'ok' : null}
            thumb={<TrailThumb proof={p} seen={seen} emptyTitle={d.card.state === 'typed' ? 'No card on file' : 'Nothing uploaded yet'} onFull={() => setFull(p)} />}
            onToggle={() => setRowOpen((cur) => ({ ...cur, [d.id]: !isOpen }))}>
            {/* NO UNDO AND NO LINE BENEATH, IN ANY STATE (Freddi, 2026-10-13: "we should also remove the
                undo button"; "remove this at the bottom of the upload area as its not needed for any
                state"). The pill and the line under the name say where the card stands. */}
            {p ? (
              <div className="big xl">
                {shot(p, alt, load, d.card.state === 'not_accepted' || d.card.state === 'paused' || d.expired)}
                {d.card.state === 'to_check' && !closed && (acting?.proofId === p.id && !busy && settling ? <DecideSkeleton /> :
                  <DecideBlock label="Valid until" acceptable={date !== null} busy={busy || acting !== null} rejectLabel="Not accepted"
                    pending={acting?.proofId === p.id ? acting.verdict : null}
                    note={p.uploadedRole === 'desk' ? 'Type the date on the card' : `Typed by ${first} · check it against the card`}
                    input={<input aria-label="Valid until" placeholder="Mar 2027" value={value} onChange={(e) => setTyped({ proofId: p.id, value: e.target.value })} />}
                    onAccept={() => date && decide(d.id, p.id, 'accept', () => onAccept(p.id, date))}
                    onReject={() => decide(d.id, p.id, 'reject', () => onReject(p.id))} />
                )}
              </div>
            ) : !closed && drop(d)}
          </TrailRow>
        );
      })}
    </SelfServiceCard>
  );
}
